<template>
  <span></span>
</template>
<script setup lang="ts">
import { watchEffect, ref } from "vue";
import { useStore } from "vuex";
import Smooch from "smooch";
import { getChatBotIntegrationId } from "@/apps/merchant3/helpers/chatBotIntegrationId.helper";
import ChatBotAuthService from "@/apps/merchant3/services/chatbot.auth.service";
import logAction from "@/apps/merchant3/services/action-logs.service";
import ActionLogs from "@/apps/merchant3/types/ActionLogEvents";
import MerchantFlags from "@/apps/merchant3/types/MerchantFlags";
import { isEcommerceFlow } from "@/apps/merchant3/helpers/checkoutFlows";
import { isSelfServiceSale } from "@/apps/merchant3/helpers/self-service";

const props = defineProps({
  chatBotMetadata: {
    type: Object as () => Record<string, string | number | null | undefined>,
    required: true,
  },
  forceChatBotInit: {
    type: Boolean,
    default: false,
  },
});

const CLERK_CHATBOT_JWT = "PJ_ClerkChatBotJwt";
const store = useStore();
const prevCartTag = ref("");
const isKiosk = sessionStorage.getItem("isKiosk");
const country = ref(props.chatBotMetadata.country);
const isChatBotAuthEnabled = ref(false);

let integrationId: string = getChatBotIntegrationId(country.value as string);

const addSmooch = (params) => {
  const hasBasicClerkInfo = () => {
    return (
      store.state.clerk?.clerk?.salesClerkId &&
      store.state.clerk?.clerk?.salesClerkName &&
      (store.state.clerk?.clerk?.chatBotApiToken ||
        isEcommerceFlow ||
        isSelfServiceSale())
    );
  };

  const hasBasicMerchantInfo = () => {
    return (
      store.state.merchant?.merchant?.merchantName &&
      store.state.merchant?.merchant?.country
    );
  };

  const shouldInitializeChatbot = () => {
    return (
      !isChatbotInitialized() &&
      (props.forceChatBotInit ||
        (hasBasicClerkInfo() &&
          hasBasicMerchantInfo() &&
          !isChatbotInitialized()))
    );
  };

  const didCartTagChanged = () => {
    return (
      store.state.carts?.cartTag &&
      prevCartTag.value !== store.state.carts?.cartTag
    );
  };

  const isChatbotInitialized = () => {
    const iframes = document.getElementsByTagName("iframe");
    for (let i = iframes.length - 1; i >= 0; i--) {
      if (iframes[i].id === "web-messenger-container") {
        return true;
      }
    }
    return false;
  };

  const getSmoochAuthToken = async () => {
    const jwt = sessionStorage.getItem(CLERK_CHATBOT_JWT);
    return jwt ? Promise.resolve(jwt) : await createSunshineJwt();
  };

  const createSunshineJwt = async () => {
    const chatBotAuthService = new ChatBotAuthService();
    return await chatBotAuthService
      .createSunshineJwt()
      .then((data) => {
        sessionStorage.setItem(CLERK_CHATBOT_JWT, data.jwt);
        logAction(ActionLogs.Events.CHATBOT_SUNSHINE_USER_AUTHENTICATED, {
          message: "Successfully created Sunshine ChatBot JWT.",
          success: true,
        });
        return data.jwt;
      })
      .catch((error) => {
        logAction(ActionLogs.Events.CHATBOT_SUNSHINE_USER_AUTHENTICATED, {
          message: "Failed to create Sunshine ChatBot JWT.",
          success: false,
          error: error,
        });
        throw error;
      });
  };

  function loadSunshineConversation(conversationId) {
    Smooch.loadConversation(conversationId)
      .then(() => {
        logAction(ActionLogs.Events.CHATBOT_SUNSHINE_USER_AUTHENTICATED, {
          message: "Successfully loaded new conversation.",
          success: true,
        });
      })
      .catch((error) => {
        logAction(ActionLogs.Events.CHATBOT_SUNSHINE_USER_AUTHENTICATED, {
          message: "Failed to load new conversation",
          success: false,
          error: error,
        });
      });
  }

  function updateSunshineUser(params) {
    Smooch.updateUser({ metadata: params })
      .then(() => {
        logAction(ActionLogs.Events.CHATBOT_SUNSHINE_USER_AUTHENTICATED, {
          message: "Successfully updated Sunshine User.",
          success: true,
        });
      })
      .catch((error) => {
        logAction(ActionLogs.Events.CHATBOT_SUNSHINE_USER_AUTHENTICATED, {
          message: "Failed to update Sunshine user",
          success: false,
          error: error,
        });
      });
  }

  function hasConversationWithMessages() {
    const conversation = Smooch.getDisplayedConversation();
    return !!conversation && !!conversation?.messages?.length;
  }

  function hasSunshineUserDifferentClerkApiToken() {
    const sunshineUser = Smooch.getUser();
    return (
      sunshineUser?.metadata?.clerkToken !==
      store.state.clerk?.clerk?.chatBotApiToken
    );
  }

  function shouldCreateNewConversation() {
    return (
      hasSunshineUserDifferentClerkApiToken() && hasConversationWithMessages()
    );
  }

  function createNewConversation() {
    return Smooch.createConversation()
      .then((conversation) => {
        logAction(ActionLogs.Events.CHATBOT_SUNSHINE_USER_AUTHENTICATED, {
          message: "Successfully logged in to the Smooch chatbot widget.",
          conversationId: conversation.id,
          success: true,
        });
        return conversation;
      })
      .catch((error) => {
        logAction(ActionLogs.Events.CHATBOT_SUNSHINE_USER_AUTHENTICATED, {
          message: "Failed to create new conversation",
          success: false,
          error: error,
        });
      });
  }

  const loginSunshine = async (clerkTag: string, authToken: string) => {
    Smooch.login(clerkTag, authToken).then(
      function () {
        logAction(ActionLogs.Events.CHATBOT_SUNSHINE_USER_AUTHENTICATED, {
          message: "Successfully logged in to the Smooch chatbot widget.",
          success: true,
        });
      },
      function (error) {
        logAction(ActionLogs.Events.CHATBOT_SUNSHINE_USER_AUTHENTICATED, {
          message: "Failed to Log in to the Smooch chatbot widget.",
          success: false,
          error: error,
        });
      }
    );
  };

  if (shouldInitializeChatbot()) {
    isChatbotInitialized() && Smooch.destroy();
    Smooch.init({
      integrationId: integrationId,
      locale: params.language,
      canUserSeeConversationList: false,
      notificationChannelPromptEnabled: false,
      browserStorage: "sessionStorage",
      integrationOrder: [],
    }).then(function () {
      Smooch.setDelegate({
        onInvalidAuth() {
          return new Promise((resolve) => {
            createSunshineJwt().then((authToken: string) => {
              resolve(authToken);
            });
          });
        },
      });

      if (!isKiosk && isChatBotAuthEnabled.value) {
        getSmoochAuthToken().then((authToken: string) => {
          loginSunshine(params.clerkTag, authToken);
        });
      }

      if (!isChatBotAuthEnabled.value) {
        Smooch.updateUser({
          givenName: params.name,
          surname: "",
          email: params.email,
          metadata: params,
        });
      }

      if (didCartTagChanged() && isChatbotInitialized()) {
        prevCartTag.value = store.state.carts?.cartTag;
        Smooch.updateUser({
          givenName: params.name,
          surname: "",
          email: params.email,
          metadata: params,
        });
      }
    });
  }

  Smooch.on("widget:opened", function () {
    if (isChatBotAuthEnabled.value) {
      if (shouldCreateNewConversation()) {
        createNewConversation().then((conversation) => {
          loadSunshineConversation(conversation.id);
        });
      }
      updateSunshineUser(params);
    }
  });
};

watchEffect(() => {
  addSmooch(props.chatBotMetadata);
  isChatBotAuthEnabled.value =
    store.state.merchant.merchantFlags !== undefined &&
    store.state.merchant.merchantFlags?.includes(
      MerchantFlags.USE_SUNSHINE_CHATBOT_AUTHENTICATION
    );
});
</script>
